import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Divider, Typography, CircularProgress } from '@mui/material';
import { apiService, extractApiErrorMessage } from '../services/apiService';
import { useProjects } from '../context/ProjectContext';
import type { AgentMessageResponse } from '../types/agent';
import {
  fetchRequestsForAgentCollectionScope,
  mapCollectionsForAgentMenus,
  type AgentCollectionOption,
} from '../utils/agentCollectionScope';
import { environmentPlaceholderSx } from '../utils/environmentUtils';
import { isAgentFailureMessage } from '../utils/agentChatMessageStyle';
import { AgentInlineSelect } from './ai-agent-ui/index';

export type AgentWidgetCopyRequestToAllCollectionsFormProps = {
  chatId?: number | null;
  defaultProjectId?: number | '';
  defaultProjectName?: string;
  defaultCollectionId?: number | '';
  defaultRequestId?: number | '';
  onClose: () => void;
  onUserMessage: (text: string) => void;
  onAgentReply: (res: AgentMessageResponse) => void | Promise<void>;
  onChatIdChange?: (chatId: number) => void;
  onLoadingChange?: (loading: boolean) => void;
};

const AgentWidgetCopyRequestToAllCollectionsForm: React.FC<
  AgentWidgetCopyRequestToAllCollectionsFormProps
> = ({
  chatId,
  defaultProjectId = '',
  defaultProjectName = '',
  defaultCollectionId = '',
  defaultRequestId = '',
  onClose,
  onUserMessage,
  onAgentReply,
  onChatIdChange,
  onLoadingChange,
}) => {
  const { setSelectedProject } = useProjects();
  const [availableProjects, setAvailableProjects] = useState<Array<{ id: number; name: string }>>([]);
  const [collections, setCollections] = useState<AgentCollectionOption[]>([]);
  const [requests, setRequests] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | ''>('');
  const [selectedRequestId, setSelectedRequestId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mergeProjectOption = useCallback(
    (list: Array<{ id: number; name: string }>) => {
      if (defaultProjectId === '' || Number.isNaN(Number(defaultProjectId))) {
        return list;
      }
      const id = Number(defaultProjectId);
      if (list.some((p) => p.id === id)) return list;
      const name = defaultProjectName.trim() || `Project ${id}`;
      return [...list, { id, name }].sort((a, b) => a.name.localeCompare(b.name));
    },
    [defaultProjectId, defaultProjectName],
  );

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const fetched = await apiService.getProjects();
      setAvailableProjects(mergeProjectOption(fetched.map((p) => ({ id: p.id, name: p.name }))));
    } catch {
      setAvailableProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, [mergeProjectOption]);

  const loadRequestsForCollection = useCallback(
    async (
      projectId: number | '',
      collectionId: number | '',
      collectionRows: AgentCollectionOption[],
    ) => {
      if (projectId === '' || collectionId === '') {
        setRequests([]);
        setSelectedRequestId('');
        return;
      }
      try {
        const mapped = await fetchRequestsForAgentCollectionScope(
          Number(projectId),
          collectionId,
          collectionRows,
        );
        const options = mapped.map((r) => ({ id: r.id, name: r.name }));
        setRequests(options);
        setSelectedRequestId((prev) => {
          if (prev !== '' && options.some((r) => r.id === prev)) return prev;
          if (defaultRequestId !== '' && options.some((r) => r.id === defaultRequestId)) {
            return defaultRequestId;
          }
          return options[0]?.id ?? '';
        });
      } catch {
        setRequests([]);
        setSelectedRequestId('');
      }
    },
    [defaultRequestId],
  );

  const loadProjectDetails = useCallback(
    async (projectId: number | '') => {
      if (projectId === '') {
        setCollections([]);
        setRequests([]);
        setSelectedCollectionId('');
        setSelectedRequestId('');
        return;
      }
      setLoadingDetails(true);
      try {
        const colls = await apiService.getCollectionsByProject(Number(projectId));
        const mappedColls = mapCollectionsForAgentMenus(colls);
        setCollections(mappedColls);

        const collectionId: number | '' =
          defaultCollectionId !== '' && mappedColls.some((c) => c.id === defaultCollectionId)
            ? defaultCollectionId
            : mappedColls.length > 0
              ? mappedColls[0].id
              : '';
        setSelectedCollectionId(collectionId);

        if (collectionId !== '') {
          await loadRequestsForCollection(projectId, collectionId, mappedColls);
        } else {
          setRequests([]);
          setSelectedRequestId('');
        }
      } catch {
        setCollections([]);
        setRequests([]);
      } finally {
        setLoadingDetails(false);
      }
    },
    [defaultCollectionId, loadRequestsForCollection],
  );

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (loadingProjects) return;
    if (defaultProjectId !== '' && availableProjects.some((p) => p.id === defaultProjectId)) {
      setSelectedProjectId(defaultProjectId);
      return;
    }
    if (availableProjects.length === 1) {
      setSelectedProjectId(availableProjects[0].id);
    }
  }, [defaultProjectId, availableProjects, loadingProjects]);

  useEffect(() => {
    if (selectedProjectId === '') return;
    void loadProjectDetails(selectedProjectId);
  }, [selectedProjectId, loadProjectDetails]);

  const handleCopy = async () => {
    if (!selectedProjectId || !selectedCollectionId || !selectedRequestId) return;
    setError(null);
    const listed = requests.find((r) => r.id === selectedRequestId);
    const requestName = listed?.name ?? `request #${selectedRequestId}`;
    const projectName =
      availableProjects.find((p) => p.id === selectedProjectId)?.name || 'selected project';
    const userText = `Copy request "${requestName}" to all collections in project "${projectName}".`;
    onUserMessage(userText);
    setSubmitting(true);
    onLoadingChange?.(true);
    try {
      const res = await apiService.agentMessage({
        action: 'copy_request_to_all_collections',
        chatId: chatId ?? undefined,
        message: userText,
        payload: {
          projectId: selectedProjectId,
          collectionId: selectedCollectionId,
          requestId: selectedRequestId,
          requestName,
        },
      });
      if (res.state?.chatId != null) {
        onChatIdChange?.(res.state.chatId as number);
      }
      if (res.state?.projectId != null) {
        try {
          const list = await apiService.getProjects();
          const match = list.find((p) => p.id === res.state!.projectId);
          if (match) setSelectedProject(match);
        } catch {
          /* ignore */
        }
      }
      const failed = Boolean(res.error) || isAgentFailureMessage(res.reply || '', res.actions);
      if (failed) {
        setError((res.reply || res.error || '').trim() || 'Could not copy the request.');
      } else {
        onClose();
      }
      await onAgentReply(res);
    } catch (err: unknown) {
      const message =
        extractApiErrorMessage(err) ||
        (err instanceof Error ? err.message : 'Could not copy the request.');
      setError(message);
      const failure: AgentMessageResponse = {
        reply: message,
        error: message,
        actions: [],
      };
      await onAgentReply(failure);
    } finally {
      setSubmitting(false);
      onLoadingChange?.(false);
    }
  };

  const busy = submitting || loadingProjects || loadingDetails;
  const fieldGapSx = { mb: 1, width: '100%' };

  return (
    <Box
      sx={{
        mt: 1,
        mb: 0.5,
        p: 1.25,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(15,23,42,0.12)',
        ...environmentPlaceholderSx,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Copy request to all collections
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Select a project and the request to copy into every other collection in that project.
      </Typography>

      <Box sx={fieldGapSx}>
        <AgentInlineSelect
          id="widget-copy-req-project"
          label="Project"
          fullWidth
          value={selectedProjectId === '' ? '' : selectedProjectId}
          disabled={busy}
          emptyLabel="No projects found"
          options={availableProjects.map((project) => ({
            value: project.id,
            label: project.name,
          }))}
          onChange={(raw) => {
            const parsed = Number(raw);
            setSelectedProjectId(Number.isNaN(parsed) || raw === '' ? '' : parsed);
          }}
        />
      </Box>

      <Box sx={fieldGapSx}>
        <AgentInlineSelect
          id="widget-copy-req-collection"
          label="Collection"
          fullWidth
          value={selectedCollectionId === '' ? '' : selectedCollectionId}
          disabled={busy || !selectedProjectId}
          emptyLabel="No collections found"
          options={collections.map((collection) => ({
            value: collection.id,
            label: collection.label,
            indent: collection.depth,
          }))}
          onChange={(raw) => {
            const parsed = Number(raw);
            const collectionId = Number.isNaN(parsed) || raw === '' ? '' : parsed;
            setSelectedCollectionId(collectionId);
            void loadRequestsForCollection(selectedProjectId, collectionId, collections);
          }}
        />
      </Box>

      <Box sx={fieldGapSx}>
        <AgentInlineSelect
          id="widget-copy-req-request"
          label="Request"
          fullWidth
          value={selectedRequestId === '' ? '' : selectedRequestId}
          disabled={busy || !selectedCollectionId}
          emptyLabel="No requests found"
          options={requests.map((request) => ({
            value: request.id,
            label: request.name,
          }))}
          onChange={(raw) => {
            const parsed = Number(raw);
            setSelectedRequestId(Number.isNaN(parsed) || raw === '' ? '' : parsed);
          }}
        />
      </Box>

      {error && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => void handleCopy()}
          disabled={busy || !selectedProjectId || !selectedCollectionId || !selectedRequestId}
          sx={{ textTransform: 'none' }}
        >
          {busy ? <CircularProgress size={16} color="inherit" /> : 'Copy'}
        </Button>
      </Box>

      <Divider sx={{ mt: 1.25 }} />
    </Box>
  );
};

export default AgentWidgetCopyRequestToAllCollectionsForm;
